import { DefaultModal } from 'UI';
import { TermEnum } from 'UI/PriceLabel/PriceLabel';
import { CourseDto, CourseDtoTypeEnum, SubscribeDtoTypeEnum } from 'api/generated';
import React from 'react';
import { useDispatch } from 'react-redux';
import { actions } from 'store';
import { PaymentOptions } from 'store/ducks/courses/types';
import styled, { css } from 'styled-components';

import {
  PLAN_START_DOLLARS,
  cdnIconForSlug,
  certCodeForSlug,
  planMonthlyDollars,
  planSavingsDollars,
} from '../BundleBuilderModal/bundleBuilderConfig';
import { useUpsellAccent } from '../BundleBuilderModal/useUpsellAccent';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** The resolved bundle, fully loaded with prices. */
  bundle: CourseDto | null;
};

/** Price amounts are whole dollars — production's convention; the payment machinery renders raw. */
const dollars = (amount?: number) =>
  typeof amount === 'number' ? `$${Math.round(amount).toLocaleString('en-US')}` : null;

/**
 * What the API actually returns per tier. The committed OpenAPI spec predates tiered pricing, so
 * the generated CoursePricesDtoInstallment is a flat single-price shape — the same staleness the
 * rest of the app works around with transformedPricing/any casts. One typed cast here, until the
 * client is regenerated.
 */
type TierPrice = {
  id?: string;
  amount?: number;
  oldAmount?: number;
  defaultCoupon?: { promoCode?: string; discountAmount?: number; discountType?: string };
  firstMonthDiscountCouponId?: string;
  firstMonthDiscountCoupon?: { promoCode?: string; discountAmount?: number; discountType?: unknown };
  /** Whole dollars, like every amount field since the dollars migration. */
  earlyFollowUpPaymentAmount?: number;
  earlyFollowUpPaymentEnabled?: boolean;
};
type TieredPrices = {
  purchase?: { gold?: TierPrice } | null;
  installment?: { gold?: TierPrice } | null;
};

/**
 * Some legacy bundles serialise a FLAT price object (amount at the top level) instead of the
 * tiered {gold: {...}} shape. ChooseProductModal normalises this; without the same fallback a
 * flat-priced bundle reached via the chooser dead-ends at "no prices configured".
 */
const goldOf = (group: unknown): TierPrice | undefined => {
  if (!group || typeof group !== 'object') return undefined;
  const g = group as { gold?: TierPrice; amount?: unknown };
  if (g.gold) return g.gold;
  return typeof g.amount === 'number' ? (group as TierPrice) : undefined;
};

/**
 * The simplified "installments or lifetime?" step between the builder and checkout.
 *
 * A bundle buyer has already decided WHAT to buy — the builder resolved their exact picks to one
 * package — so the full three-column pricing table would re-ask a question that no longer exists
 * (bundles sell at one level). Only HOW to pay remains: two buttons.
 *
 * The handoff is byte-compatible with what the pricing table dispatches
 * (handlePricingTablePurchase): same CourseOptionsForPayment shape, same three actions, so
 * PaymentModal and everything behind it is reused untouched.
 */
const BundlePaymentChoiceModal = ({ isOpen, onClose, bundle }: Props) => {
  const dispatch = useDispatch();
  const { vars: accentVars } = useUpsellAccent();

  const prices = (bundle?.prices ?? null) as TieredPrices | null;

  // The member certifications' brand marks, same as the builder's package cards. The relation is
  // on the payload (getManyPublicByIds loads it) but the stale generated types don't know it.
  const memberSlugs = (
    (bundle as unknown as { bundleCoursesAsBundle?: Array<{ course?: { slug?: string } }> } | null)
      ?.bundleCoursesAsBundle ?? []
  )
    .map((m) => m.course?.slug)
    .filter((slug): slug is string => Boolean(slug));
  const purchase = goldOf(prices?.purchase);
  const installment = goldOf(prices?.installment);

  /**
   * NET of coupons — what the learner actually pays. Production prices are sticker + coupon
   * (Essential lifetime: $1,598 − SUMMER799 = $799); checkout applies the coupons server-side,
   * so quoting the sticker here would advertise double the real price.
   */
  const netOfCoupon = (amount?: number, coupon?: { discountAmount?: number; discountType?: string }) => {
    if (typeof amount !== 'number') return undefined;
    if (!coupon?.discountAmount) return amount;
    if (coupon.discountType === 'percentage') return Math.round(amount - (amount * coupon.discountAmount) / 100);
    return Math.round(amount - coupon.discountAmount);
  };
  const purchaseNet = netOfCoupon(purchase?.amount, purchase?.defaultCoupon);
  const planNetAfterMain = netOfCoupon(installment?.amount, installment?.defaultCoupon);
  const planNet = netOfCoupon(planNetAfterMain, installment?.firstMonthDiscountCoupon as never);

  const openCheckout = (paymentType: 'onetime' | 'plan') => {
    const priceData = paymentType === 'plan' ? installment : purchase;
    if (!bundle || !priceData?.amount) return;

    dispatch(
      actions.courses.setPaymentOption(paymentType === 'plan' ? PaymentOptions.PAYMENT_PLAN : PaymentOptions.ONETIME),
    );
    dispatch(
      actions.courses.selectCourseOptionsForPayment({
        term: paymentType === 'plan' ? TermEnum.INSTALLMENT : TermEnum.LIFE_TIME,
        type: CourseDtoTypeEnum.Certificate,
        courseId: bundle.id,
        subscribeType: SubscribeDtoTypeEnum.Gold,
        amount: Number(priceData.amount),
        oldAmount: priceData.oldAmount ?? undefined,
        isUpdate: false,
        isDowngrade: false,
        couponCode: priceData.defaultCoupon?.promoCode,
        firstMonthDiscountId: priceData.firstMonthDiscountCouponId,
        firstMonthDiscountCode: priceData.firstMonthDiscountCoupon?.promoCode,
        firstMonthDiscountAmount: priceData.firstMonthDiscountCoupon?.discountAmount,
        firstMonthDiscountType: priceData.firstMonthDiscountCoupon?.discountType as never,
        firstMonthDiscountPromoCode: priceData.firstMonthDiscountCoupon?.promoCode,
      }),
    );
    dispatch(actions.courses.switchToPaymentFromPaymentChoice());
  };

  /**
   * Quote the plan the learner will actually get.
   *
   * Builder packages run the advertised schedule — $49 start (via first-month coupon),
   * then 11 monthly payments — quoting $129/$109/$89.
   * Legacy bundles have neither coupon: their plan is twelve equal payments.
   */
  const hasFirstMonthDiscount = Boolean(
    installment?.firstMonthDiscountCouponId || installment?.firstMonthDiscountCoupon,
  );
  const monthly = hasFirstMonthDiscount
    ? planMonthlyDollars(planNet, installment?.earlyFollowUpPaymentAmount)
    : typeof planNetAfterMain === 'number'
    ? Math.round(planNetAfterMain / 12)
    : undefined;
  const saved = planSavingsDollars(purchaseNet, planNet);

  return (
    <DefaultModal
      isOpen={isOpen}
      onRequestClose={onClose}
      title="How would you like to pay?"
      containerCSS={ContainerCSS}
      headerContainerCSS={HeaderCSS}
      titleCSS={TitleCSS}
      closeButtonCSS={CloseCSS}>
      <Body style={accentVars as React.CSSProperties}>
        {bundle && (
          <BundleHeader>
            {/* The chips ARE the contents list — showing the bundle name above them printed the
                same certification codes twice (legacy bundles are literally named "CPT + CNS").
                The name only appears when there are no members to show instead. */}
            {memberSlugs.length > 0 ? (
              <CertChips>
                {memberSlugs.map((slug) => (
                  <CertChip key={slug}>
                    <CertChipIcon src={cdnIconForSlug(slug)} alt="" loading="lazy" />
                    {certCodeForSlug(slug)}
                  </CertChip>
                ))}
              </CertChips>
            ) : (
              <BundleName>{bundle.name}</BundleName>
            )}
          </BundleHeader>
        )}

        <Options>
          {installment?.amount ? (
            <OptionCard type="button" $primary onClick={() => openCheckout('plan')}>
              <Badge>Most popular</Badge>
              <OptionTitle>Payment plan</OptionTitle>
              <OptionPrice>
                {hasFirstMonthDiscount ? (
                  <>
                    Start for ${PLAN_START_DOLLARS}
                    <OptionPriceSub> then {monthly != null ? `$${monthly}` : ''}/mo</OptionPriceSub>
                  </>
                ) : (
                  <>{monthly != null ? `$${monthly}` : ''}/mo</>
                )}
              </OptionPrice>
              <Benefits>
                <li>
                  {hasFirstMonthDiscount ? `Start studying today for $${PLAN_START_DOLLARS}` : 'Start studying today'}
                </li>
                <li>Spread the cost over monthly payments</li>
                <li>Full schedule shown before you confirm anything</li>
              </Benefits>
              <OptionCta $primary>Continue with the plan →</OptionCta>
            </OptionCard>
          ) : null}

          {purchase?.amount ? (
            <OptionCard type="button" onClick={() => openCheckout('onetime')}>
              <Badge $ink>Best price</Badge>
              <OptionTitle>One-time payment</OptionTitle>
              <OptionPrice>
                {dollars(purchaseNet)}
                {saved != null && <OptionPriceSub> — save ${saved}</OptionPriceSub>}
              </OptionPrice>
              <Benefits>
                <li>The lowest total price — nothing added later</li>
                <li>Pay once, own it for life</li>
                <li>No future charges to think about</li>
              </Benefits>
              <OptionCta>Pay once →</OptionCta>
            </OptionCard>
          ) : null}
        </Options>

        {!purchase?.amount && !installment?.amount && (
          <Message role="alert">This package has no prices configured yet. Please contact support.</Message>
        )}
      </Body>
    </DefaultModal>
  );
};

export default BundlePaymentChoiceModal;

const ContainerCSS = css`
  /* Match UpgradeChooserModal so the chooser -> payment-choice steps keep one footprint. */
  max-width: 780px;
  width: 94vw;
  padding: 32px 36px 36px;
`;

/** Reserve the absolutely-positioned close button's footprint so the centred title clears it. */
const HeaderCSS = css`
  padding: 0 48px;
  margin-bottom: 20px;
`;

/** The default modal title is sized for wide modals and clips here — wrap instead of overflowing. */
const TitleCSS = css`
  font-size: 26px;
  line-height: 1.25;
  white-space: normal;
  text-align: center;
`;

const CloseCSS = css`
  top: 26px;
  right: 24px;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const BundleHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const BundleName = styled.div`
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  color: #6b7280;
`;

const CertChips = styled.span`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
`;

const CertChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px 2px 4px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  white-space: nowrap;
  color: #4b5563;
  background: #f3f4f6;
`;

const CertChipIcon = styled.img`
  height: 10px;
  width: auto;
`;

const Options = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-items: stretch;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const OptionCard = styled.button<{ $primary?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  text-align: left;
  padding: 22px 20px 18px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: ${({ $primary }) => ($primary ? 'var(--accent-soft, rgba(237, 90, 38, 0.05))' : '#fff')};
  border: 2px solid ${({ $primary }) => ($primary ? 'var(--accent, #ED5A26)' : '#e5e7eb')};

  &:hover {
    border-color: var(--accent, #ed5a26);
    transform: translateY(-2px);
    box-shadow: 0 10px 24px var(--accent-glow, rgba(237, 90, 38, 0.16));
  }
`;

const Badge = styled.span<{ $ink?: boolean }>`
  position: absolute;
  top: -10px;
  left: 20px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #fff;
  background: ${({ $ink }) => ($ink ? '#141826' : 'var(--accent, #ED5A26)')};
`;

const OptionTitle = styled.div`
  font-size: 17px;
  font-weight: 800;
  color: #141826;
`;

const OptionPrice = styled.div`
  margin-top: 2px;
  font-size: 22px;
  font-weight: 800;
  color: var(--accent, #ed5a26);
`;

const OptionPriceSub = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #22be74;
`;

const Benefits = styled.ul`
  margin: 12px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;

  li {
    position: relative;
    padding-left: 22px;
    font-size: 13px;
    line-height: 1.4;
    color: #374151;

    &::before {
      content: '✓';
      position: absolute;
      left: 0;
      top: 0;
      font-weight: 800;
      color: #22be74;
    }
  }
`;

const OptionCta = styled.div<{ $primary?: boolean }>`
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid ${({ $primary }) => ($primary ? 'var(--accent-border, rgba(237, 90, 38, 0.25))' : '#f3f4f6')};
  font-size: 14px;
  font-weight: 800;
  color: ${({ $primary }) => ($primary ? 'var(--accent, #ED5A26)' : '#141826')};
`;

const Message = styled.p`
  margin: 0;
  padding: 8px 4px;
  font-size: 14px;
  color: #b91c1c;
  text-align: center;
`;
